/**
 * Cargar un movimiento suelto: un gasto, un prestamo, un interes. Desde la
 * pantalla de Socios, el mismo dialogo carga retiros y aportes, y pregunta de
 * que socio son, sin ninguno marcado.
 *
 * LAS DOS DECISIONES DE ESTE FORMULARIO
 * -------------------------------------
 * 1. La moneda NO se elige. Se elige la cuenta, y la cuenta trae su moneda. El
 *    `<InputImporte />` va sin `onMonedaChange`, asi que el selector queda
 *    deshabilitado a la vista: se ve cual es la moneda y se ve que no se toca.
 * 2. Antes de escribir hay un paso de revision con el resumen completo. Un
 *    movimiento de caja no se deshace con Ctrl+Z.
 *
 * El orden de las preguntas es el de la plata: primero si entra o sale, y
 * recien ahi la categoria, que solo ofrece las de ese lado. Las categorias van
 * como chips a la vista y no en un desplegable: son pocas, y elegir es un toque.
 *
 * (0156) PRIMERO «QUÉ FUE». Se escribe lo que paso («agua», «cloro») y la
 * categoria se propone sola, con la marca de que es una propuesta: elegir de
 * una lista es donde se equivocaban. Sin nada escrito no viene ninguna marcada.
 * Los gastos de todos los meses estan a un toque, y un «Otro» grande pide
 * confirmar que es un gasto del local.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { formatear } from "@tora/shared";

import { ChipMoneda, InputImporte } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Segmented } from "@/components/ui/segmented";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { ChipsSocio } from "@/components/socios/ChipsSocio";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import type { CuentaFinanciera, NuevoMovimientoCaja, Proveedor, Socio } from "@/lib/tipos";

import {
  ETIQUETA_TIPO_CUENTA,
  buscarOperacion,
  esDeSocio,
  esOtroGrande,
  gastosFrecuentes,
  operacionesDe,
  proponerCategoria,
  sugerirCategoria,
  OTRO_GRANDE,
  type Frecuente,
  type AmbitoMovimiento,
  type Operacion,
} from "./logica";

// Cortas: «Egreso · sale plata» no entraba a 390 px y se cortaba contra el
// borde del dialogo. Que entra o sale plata lo dicen la flecha y el resumen.
const LADOS = [
  { valor: "INGRESO", etiqueta: "Ingreso", icono: ArrowDownLeft },
  { valor: "EGRESO", etiqueta: "Egreso", icono: ArrowUpRight },
] as const;

export interface DialogMovimientoProps {
  abierto: boolean;
  onAbiertoChange: (abierto: boolean) => void;
  cuentas: ReadonlyArray<CuentaFinanciera>;
  /**
   * Los proveedores, para avisar cuando el detalle dice que es un pago a uno de
   * ellos (0155): ese pago se hace desde Proveedores, donde baja la deuda.
   */
  proveedores?: ReadonlyArray<Pick<Proveedor, "id" | "nombre">>;
  /**
   * "caja" (el default) ofrece los gastos, los pagos y los prestamos; "socios",
   * solo el retiro y el aporte de un socio. Ver `AmbitoMovimiento`.
   */
  ambito?: AmbitoMovimiento;
  /** Con `ambito="socios"`: de quien es la plata. */
  socios?: ReadonlyArray<Socio>;
  /**
   * La clasificacion con que se abre (una `clave` de `OPERACIONES`). Desde
   * Socios, "Aporte" ya sabe que es un aporte. El lado (ingreso o egreso) sale
   * de la operacion.
   */
  operacionInicial?: string;
  /** El socio con que se abre. Solo si quien abre ya lo sabe; si no, ninguno. */
  socioInicial?: number;
  /**
   * Los saldos de las cajas (lo que devuelve `/cajas`), para avisar en la
   * revision si un egreso deja la caja en negativo. Opcional: sin saldos no se
   * avisa, como antes.
   */
  saldos?: ReadonlyArray<{ id: number; saldo: number | string }>;
  /**
   * Lo que mas se carga a mano (`GET /cajas/frecuentes`, 0156): con esto se
   * propone la categoria al escribir «qué fue» y salen los gastos frecuentes.
   */
  frecuentes?: ReadonlyArray<Frecuente>;
  guardando?: boolean;
  onConfirmar: (datos: NuevoMovimientoCaja) => void;
}

export function DialogMovimiento({
  abierto,
  onAbiertoChange,
  cuentas,
  proveedores = [],
  ambito = "caja",
  socios = [],
  operacionInicial,
  socioInicial,
  saldos = [],
  frecuentes = [],
  guardando = false,
  onConfirmar,
}: DialogMovimientoProps): JSX.Element {
  const [paso, setPaso] = useState<"carga" | "revision">("carga");
  const [cuentaId, setCuentaId] = useState<string>("");
  const [tipo, setTipo] = useState<Operacion["tipo"]>("EGRESO");
  const [operacion, setOperacion] = useState<string>("");
  const [socioId, setSocioId] = useState<string>("");
  const [importe, setImporte] = useState<number | null>(null);
  const [detalle, setDetalle] = useState("");
  const [confirmaOtro, setConfirmaOtro] = useState(false);

  // Al abrir se arranca limpio: un formulario que recuerda el gasto anterior
  // termina cargando el mismo gasto dos veces. Depende de `abierto` y de lo que
  // pide quien abre (dos valores sueltos, que no cambian con un refetch). NO de
  // `cuentas`: cada vez que la lista se refresca (cambio un saldo) borraba lo
  // cargado a mitad de camino, y el importe quedaba a la vista pero vacio por
  // dentro, con «Revisar» apagado sin explicacion.
  useEffect(() => {
    // El importe se vacia tambien al CERRAR. `InputImporte` arma su texto con
    // el valor que tiene al montarse, y este efecto corre despues: al reabrir
    // mostraba el importe de la vez anterior (aunque se hubiera cancelado),
    // vacio por dentro, y al salir del campo lo volvia a mandar como valido.
    if (!abierto) {
      setImporte(null);
      return;
    }
    setPaso("carga");
    // La caja tambien: quedaba la de la vez anterior, y un sueldo en pesos
    // cargado despues de un movimiento en dolares salia como US$ 200.000. La
    // vacia el efecto de abajo la completa con la primera.
    setCuentaId("");
    const inicial = operacionInicial ? buscarOperacion(operacionInicial) : undefined;
    if (inicial) setTipo(inicial.tipo);
    setOperacion(inicial?.clave ?? "");
    setSocioId(socioInicial ? String(socioInicial) : "");
    setImporte(null);
    setDetalle("");
    setConfirmaOtro(false);
  }, [abierto, operacionInicial, socioInicial]);

  // La cuenta por defecto es la primera, apenas haya cuentas. Si ya hay una
  // elegida en esta apertura, no se toca (un refetch no la cambia).
  useEffect(() => {
    if (abierto) setCuentaId((prev) => prev || String(cuentas[0]?.id ?? ""));
  }, [abierto, cuentas]);

  const cuenta = useMemo(() => cuentas.find((c) => String(c.id) === cuentaId), [cuentas, cuentaId]);
  const clasificaciones = operacionesDe(tipo, ambito);
  // La categoria elegida a mano manda; si no hay, la que propone «qué fue».
  const propuesta = operacion ? null : proponerCategoria(detalle, tipo, ambito, frecuentes);
  const op = buscarOperacion(operacion || propuesta || "");
  const moneda = cuenta?.moneda ?? "ARS";
  // "Otro" sin detalle es un asiento que despues nadie sabe explicar.
  const faltaDetalle = op?.concepto === "OTRO" && !detalle.trim();
  const sugerencia = sugerirCategoria(detalle, op, proveedores);
  const opSugerida = sugerencia?.clave ? buscarOperacion(sugerencia.clave) : undefined;
  // La sugerencia ofrece cambiar solo si la categoria esta en este dialogo.
  const sePuedeCambiar = Boolean(opSugerida && esDeSocio(opSugerida) === (ambito === "socios"));
  const pideSocio = esDeSocio(op);
  // Desde Socios el boton que se toco ya dijo si es retiro o aporte: preguntar
  // de nuevo "ingreso o egreso" y ofrecer una sola categoria es ruido. Queda
  // lo que falta saber: de quien, de que caja, cuanto.
  const categoriaFija = ambito === "socios" && Boolean(operacionInicial) && Boolean(op);
  const socio = socios.find((s) => String(s.id) === socioId);
  const faltaSocio = pideSocio && !socio;
  const listo = Boolean(
    cuenta && op && importe !== null && importe > 0 && !faltaDetalle && !faltaSocio,
  );
  const otroGrande = esOtroGrande(op, importe, moneda);

  // Un egreso que deja la caja en rojo se avisa en la revision, sin bloquear:
  // un saldo puede estar mal cargado y el movimiento igual haber pasado. Antes
  // un retiro de socio de $ 10.000 desde un Banco en $ 0 se guardaba callado, y
  // recien en Caja aparecia el -$ 10.000.
  const filaSaldo = cuenta ? saldos.find((s) => s.id === cuenta.id) : undefined;
  const saldoAntes = filaSaldo ? Number(filaSaldo.saldo) : null;
  const quedaNegativo =
    op?.tipo === "EGRESO" &&
    saldoAntes !== null &&
    Number.isFinite(saldoAntes) &&
    importe !== null &&
    saldoAntes - importe < 0;

  function cambiarTipo(nuevo: Operacion["tipo"]): void {
    setTipo(nuevo);
    // La categoria elegida era del otro lado: hay que volver a elegir.
    setOperacion("");
  }

  const frecuentesVisibles =
    ambito === "caja" && tipo === "EGRESO" && !categoriaFija ? gastosFrecuentes(frecuentes) : [];

  function confirmar(): void {
    if (!cuenta || !op || importe === null) return;
    onConfirmar({
      cuenta_id: cuenta.id,
      tipo: op.tipo,
      concepto: op.concepto,
      importe,
      ...(pideSocio && socio ? { socio_id: socio.id } : {}),
      ...(detalle.trim() ? { detalle: detalle.trim() } : {}),
    });
  }

  return (
    <Dialog open={abierto} onOpenChange={onAbiertoChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {paso === "revision"
              ? "Revisá antes de guardar"
              : categoriaFija
                ? op?.label
                : "Registrar movimiento"}
          </DialogTitle>
          <DialogDescription>
            {paso === "revision"
              ? "Esto escribe en la caja. Una vez guardado queda como asiento."
              : categoriaFija
                ? op?.ayuda
                : "La moneda la pone la cuenta que elijas. No se puede contradecir."}
          </DialogDescription>
        </DialogHeader>

        {paso === "carga" ? (
          <div className="space-y-4">
            {categoriaFija ? (
              <ChipsSocio socios={socios} valor={socioId} onChange={setSocioId} />
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="mov-cuenta">
                {categoriaFija
                  ? op?.tipo === "INGRESO"
                    ? "¿A qué caja entra?"
                    : "¿De qué caja sale?"
                  : "Cuenta"}
              </Label>
              <Select value={cuentaId} onValueChange={setCuentaId}>
                <SelectTrigger id="mov-cuenta" aria-label="Cuenta">
                  <SelectValue placeholder="Elegí una cuenta" />
                </SelectTrigger>
                <SelectContent>
                  {cuentas.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.nombre} · {c.moneda}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {cuenta ? (
                <p className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                  {ETIQUETA_TIPO_CUENTA[cuenta.tipo] ?? cuenta.tipo} en{" "}
                  {cuenta.moneda === "ARS" ? "pesos" : "dólares"}
                  <ChipMoneda moneda={cuenta.moneda} />. El importe queda en esa moneda.
                </p>
              ) : null}
            </div>

            {categoriaFija ? null : (
              <>
                <div className="space-y-1.5">
                  <Label>Movimiento</Label>
                  <Segmented
                    etiqueta="Ingreso o egreso"
                    opciones={LADOS}
                    valor={tipo}
                    onChange={cambiarTipo}
                    tamano="md"
                    className="flex w-full [&>button]:flex-1 [&>button]:justify-center"
                  />
                </div>

                {frecuentesVisibles.length > 0 ? (
                  <div className="space-y-1.5">
                    <Label>Gastos frecuentes</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {frecuentesVisibles.map((g) => (
                        <Button
                          key={g.etiqueta}
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setTipo("EGRESO");
                            setOperacion(g.clave);
                            setDetalle(g.detalle);
                          }}
                        >
                          {g.etiqueta}
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : null}

                <Campo label="¿Qué fue?" requerido={op?.concepto === "OTRO"}>
                  <Input
                    id="mov-detalle"
                    value={detalle}
                    onChange={(e) => setDetalle(e.target.value)}
                    placeholder="Ej: agua, alquiler, vianda, cloro"
                    autoComplete="off"
                  />
                </Campo>

                <div className="space-y-1.5">
                  <Label>
                    {tipo === "INGRESO" ? "Categoría del ingreso" : "Categoría del egreso"}
                  </Label>
                  <Segmented
                    apariencia="chips"
                    tamano="sm"
                    etiqueta="Categoría"
                    opciones={clasificaciones.map((o) => ({ valor: o.clave, etiqueta: o.label }))}
                    valor={operacion || propuesta || ""}
                    onChange={setOperacion}
                  />
                  {propuesta && op ? (
                    <p className="text-xs text-muted-foreground">
                      Propuesta por lo que escribiste: «{op.label}». Si no es, elegí otra.
                    </p>
                  ) : op ? (
                    <p className="text-xs text-muted-foreground">{op.ayuda}</p>
                  ) : null}
                </div>
              </>
            )}

            {ambito === "caja" && tipo === "EGRESO" && !categoriaFija ? (
              <p className="text-xs text-muted-foreground">
                ¿Le pagás a un proveedor? Hacelo desde{" "}
                <Link to="/proveedores" className="underline underline-offset-2">
                  Proveedores
                </Link>
                : ahí baja lo que se le debe.
              </p>
            ) : null}

            {pideSocio && !categoriaFija ? (
              <ChipsSocio socios={socios} valor={socioId} onChange={setSocioId} />
            ) : null}

            <InputImporte
              label="Importe"
              valor={importe}
              onValorChange={setImporte}
              moneda={moneda}
              requerido
              autoFocus={categoriaFija}
            />

            {categoriaFija ? (
              <Campo label="Detalle" requerido={op?.concepto === "OTRO"}>
                <Input
                  id="mov-detalle"
                  value={detalle}
                  onChange={(e) => setDetalle(e.target.value)}
                  placeholder="Qué fue este movimiento"
                  autoComplete="off"
                />
              </Campo>
            ) : null}

            {sugerencia ? (
              <Aviso tono="warning" compacto>
                <span>{sugerencia.texto}</span>
                {sePuedeCambiar && opSugerida ? (
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto px-1 py-0 sm:h-auto"
                    onClick={() => {
                      setTipo(opSugerida.tipo);
                      setOperacion(opSugerida.clave);
                    }}
                  >
                    Cambiar a «{opSugerida.label}»
                  </Button>
                ) : null}
              </Aviso>
            ) : null}
          </div>
        ) : (
          <div className="space-y-3">
            <ResumenMovimiento
              cuenta={cuenta?.nombre ?? ""}
              operacion={op?.label ?? ""}
              socio={pideSocio ? socio?.nombre : undefined}
              tipo={op?.tipo ?? "EGRESO"}
              importe={importe ?? 0}
              moneda={moneda}
              detalle={detalle.trim()}
              saldo={
                quedaNegativo && saldoAntes !== null && importe !== null
                  ? { antes: saldoAntes, despues: saldoAntes - importe }
                  : undefined
              }
            />
            {otroGrande && importe !== null ? (
              <Aviso tono="warning">
                <p>
                  Esto resta {formatear(importe, moneda)} de la ganancia del mes. «Otro» es para un
                  gasto del local que no entra en ninguna categoría (más de{" "}
                  {formatear(OTRO_GRANDE[moneda], moneda)} se confirma).
                </p>
                <p className="mt-1">
                  Si es un pago a un proveedor, va por Proveedores; si es plata que se lleva un
                  socio, es un retiro.
                </p>
                <Checkbox
                  className="mt-2"
                  checked={confirmaOtro}
                  onChange={(e) => setConfirmaOtro(e.target.checked)}
                  label="Es un gasto del local"
                />
              </Aviso>
            ) : null}
          </div>
        )}

        <DialogFooter>
          {paso === "carga" ? (
            <>
              <Button type="button" variant="ghost" onClick={() => onAbiertoChange(false)}>
                Cancelar
              </Button>
              <Button type="button" disabled={!listo} onClick={() => setPaso("revision")}>
                Revisar
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => setPaso("carga")}>
                <ArrowLeft aria-hidden="true" />
                Volver
              </Button>
              <Button
                type="button"
                cargando={guardando}
                disabled={otroGrande && !confirmaOtro}
                onClick={confirmar}
              >
                Confirmar y guardar
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResumenMovimiento({
  cuenta,
  operacion,
  socio,
  tipo,
  importe,
  moneda,
  detalle,
  saldo,
}: {
  cuenta: string;
  operacion: string;
  socio?: string;
  tipo: "INGRESO" | "EGRESO";
  importe: number;
  moneda: "ARS" | "USD";
  detalle: string;
  /** Solo cuando el egreso deja la caja en negativo. */
  saldo?: { antes: number; despues: number };
}): JSX.Element {
  const Icono = tipo === "INGRESO" ? ArrowDownLeft : ArrowUpRight;
  return (
    <div className="space-y-3">
      <Card variant="muted" padding="normal" className="space-y-3">
        <div className="flex items-center gap-2">
          <Icono
            className={cn("size-4", tipo === "INGRESO" ? "text-success" : "text-destructive")}
            aria-hidden="true"
          />
          <span className="text-sm font-medium">
            {operacion} · {tipo === "INGRESO" ? "entra plata" : "sale plata"}
          </span>
        </div>

        <dl className="space-y-2 text-sm">
          <Fila termino="Cuenta" valor={cuenta} />
          {socio ? <Fila termino="Socio" valor={socio} /> : null}
          {/*
          El importe del resumen se escribe como UNA frase ("− $ 45.000,00 ARS")
          y no como <Importe>: el signo, el numero y el codigo tienen que ir en
          un solo nodo de texto para poder leerse y buscarse de corrido (es lo
          que fija caja.test). Sin color a mano: la moneda ya esta dicha.
        */}
          <Fila
            termino="Importe"
            valor={
              <span className="font-medium tabular-nums">
                {tipo === "EGRESO" ? "− " : "+ "}
                {formatear(importe, moneda)} {moneda}
              </span>
            }
          />
          {detalle ? <Fila termino="Detalle" valor={detalle} /> : null}
        </dl>
      </Card>
      {saldo ? (
        <Aviso tono="warning" compacto>
          {cuenta} tiene {formatear(saldo.antes, moneda)} y queda en{" "}
          {formatear(saldo.despues, moneda)}. Revisá que sea la caja correcta; si lo es, se puede
          guardar igual.
        </Aviso>
      ) : null}
    </div>
  );
}

/**
 * Una linea de resumen "termino: valor". La comparten los dialogos de caja y
 * el de cliente; el termino va como eyebrow y el valor a la derecha.
 */
export function Fila({ termino, valor }: { termino: string; valor: React.ReactNode }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0">
        <TituloSeccion nivel="etiqueta" as="span">
          {termino}
        </TituloSeccion>
      </dt>
      <dd className="min-w-0 break-words text-right text-sm">{valor}</dd>
    </div>
  );
}
